import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class UserService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/auth`,
      withCredentials: true
    });

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Server responded with an error status
          console.error('Server response error:', error.response.data);
          throw error;
        } else if (error.request) {
          // Request was made but no response received
          console.error('No response received from server');
          throw new Error('Could not connect to server. Please check your connection.');
        } else {
          // Something happened in setting up the request
          console.error('Request setup error:', error.message);
          throw error;
        }
      }
    );
  }

  // Add token to request headers
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Validate token and get user data
  async validateToken() {
    try {
      const response = await this.api.get('/validate');
      return response.data.data;
    } catch (error) {
      console.error('Error validating token:', error);
      throw error;
    }
  }

  // Register user in backend
  async register({ uid, email, name }) {
    try {
      const response = await this.api.post('/register', {
        uid,
        email,
        name
      });
      return response.data.data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  }

  // Verify email with verification code
  async verifyEmail(code, uid) {
    try {
      const response = await this.api.post('/verify-email', {
        code,
        uid
      });
      return response.data.data;
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  }

  // Resend verification email
  async resendVerificationEmail(uid) {
    try {
      const response = await this.api.post('/resend-verification', {
        uid
      });
      return response.data.data;
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  }

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await this.api.put('/profile', userData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Get is user authenticated
  async isUserAuthenticated() {
    try {
      return !!this.api.defaults.headers.common['Authorization'];
    } catch (error) {
      return false;
    }
  }
}

export const userService = new UserService();
